function toFiniteEtaSeconds(value) {
	if (!Number.isFinite(value)) return null;
	return Math.max(0, Math.round(value));
}

export function formatEta(seconds) {
	const etaSeconds = toFiniteEtaSeconds(seconds);
	if (etaSeconds == null) return null;
	if (etaSeconds < 60) return `${etaSeconds}s`;
	return `${Math.floor(etaSeconds / 60)}m`;
}

export function normalizeEtaSeconds(value) {
	return toFiniteEtaSeconds(value);
}

// Magnitude phrase for a duration in seconds: "42 s" / "5 min" / "1 h 5 min".
//
// Hours and minutes are both derived from ONE rounded total-minutes value.
// Rounding each component independently produces impossible readings — at
// 1h59m30s, `Math.round((7170 % 3600) / 60)` is 60, giving "1 h 60 min"; the
// same rounding at 59m30s gives "60 min" instead of "1 h".
function humanizeDuration(totalSeconds) {
	if (totalSeconds < 60) return `${totalSeconds} s`;
	const totalMinutes = Math.round(totalSeconds / 60);
	if (totalMinutes < 60) return `${totalMinutes} min`;
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
}

// Verbose magnitude phrase for the progress-card ETA line, e.g.
// "~42 s" / "~5 min" / "~1 h 5 min". Kept separate from formatEta (which is
// the compact "42s"/"1m" form).
export function formatEtaDuration(seconds) {
	const etaSeconds = toFiniteEtaSeconds(seconds);
	if (etaSeconds == null) return null;
	return `~${humanizeDuration(etaSeconds)}`;
}

// Elapsed-time phrase for the progress card, e.g. "42 s elapsed" / "5 min
// elapsed". Shown when no ETA is available: an indefinite "Calculating…" reads
// as a frozen job, while a climbing counter proves the run is alive without
// inventing a prediction we don't have.
export function elapsedLineText(state, elapsedSeconds, t) {
	if (state !== "running") return null;
	const duration = toFiniteEtaSeconds(elapsedSeconds);
	if (duration == null) return null;
	const formatted = humanizeDuration(duration);
	return t("progressNotifications.etaElapsed", {
		duration: formatted,
		defaultValue: `${formatted} elapsed`,
	});
}

// The estimate ran out while the job still runs: say so, with the time it has really taken,
// rather than switching to a bare elapsed counter that reads as a broken estimate.
export function overrunLineText(state, elapsedSeconds, t) {
	if (state !== "running") return null;
	const duration = toFiniteEtaSeconds(elapsedSeconds);
	if (duration == null) return null;
	const formatted = humanizeDuration(duration);
	return t("progressNotifications.etaOverrun", {
		duration: formatted,
		defaultValue: `Taking longer than expected · ${formatted} elapsed`,
	});
}

// Decision for the card's secondary ETA line. Pure so it's unit-testable.
// When ETA is unknown (no historical data for this user / job weight) we
// show nothing rather than "Calculating…" — the label was confusing users
// into thinking the job was frozen. Progress is already communicated by the
// N/Total counter and the animated spinner on the card icon.
export function etaLineText(state, current, etaSeconds, t) {
	if (state !== "running") return null;
	// A spent estimate on a job that is still running says nothing true: stay
	// silent so the card falls back to its elapsed line instead of "~0 s remaining".
	if (typeof etaSeconds === "number" && etaSeconds <= 0) return null;
	const duration = formatEtaDuration(etaSeconds);
	if (duration == null) return null;  // ETA unknown — stay silent
	return t("progressNotifications.etaRemaining", {
		duration,
		defaultValue: `${duration} remaining`,
	});
}
